import { useEffect, useMemo, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  ClipboardList, Plus, Pencil, MoreVertical, Send, Ban, CheckCircle2, XCircle, Trash2,
  UserPlus, Search, Filter, Clock, Users2, Building2, Eye, FileDown,
  CalendarCheck, History as HistoryIcon, Briefcase, Printer, Share2, Link2, Globe,
} from "lucide-react";
import { BRL, fmtDate, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

const VAC_STATUS = {
  solicitada:       { label: "Solicitada", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  em_aprovacao:     { label: "Em aprovação", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  aprovada:         { label: "Aprovada", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  reprovada:        { label: "Reprovada", cls: "bg-red-100 text-red-700 border-red-200" },
  em_recrutamento:  { label: "Em recrutamento", cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  em_triagem:       { label: "Em triagem", cls: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  em_entrevista:    { label: "Em entrevista", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  encerrada:        { label: "Encerrada", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelada:        { label: "Cancelada", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const CAND_STATUS = {
  triagem:            "Triagem",
  entrevista_rh:      "Entrevista RH",
  entrevista_gestor:  "Entrevista Gestor",
  teste:              "Teste",
  documentacao:       "Documentação",
  exame_medico:       "Exame Médico",
  aprovado:           "Aprovado",
  reprovado:          "Reprovado",
  contratado:         "Contratado",
  banco_talentos:     "Banco de talentos",
};

const emptyVac = {
  company_id: "", sector_id: "", title: "", cargo: "", motivo: "aumento_quadro",
  tipo_contrato: "CLT", quantidade: 1, salario_previsto: 0, urgencia: "media",
  requisitos: "", escolaridade: "", experiencia: "", descricao: "", local_trabalho: "",
  gestor_solicitante: "", publicada: false, status: "solicitada", observacoes: "",
};

const emptyCand = {
  vacancy_id: "", nome: "", email: "", telefone: "", cidade: "",
  escolaridade: "", experiencia_anos: 0, origem: "indicacao",
  curriculo_url: "", pretensao_salarial: 0, parecer_rh: "", parecer_gestor: "",
  status: "triagem", observacoes: "",
};

export default function RecruitmentPage() {
  const { companies, companyParam } = useCompany();
  const { hasRole } = useAuth();
  const canEdit = hasRole("rh_admin", "rh_operacional", "gestor_setor");
  const canAdmin = hasRole("rh_admin");
  const [tab, setTab] = useState("vagas");
  const [dash, setDash] = useState(null);
  const [vacs, setVacs] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [cands, setCands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Dialog state
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVac);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  // Candidate
  const [cOpen, setCOpen] = useState(false);
  const [cEditing, setCEditing] = useState(null);
  const [cForm, setCForm] = useState(emptyCand);

  // Hire dialog
  const [hireOpen, setHireOpen] = useState(false);
  const [hireCandidate, setHireCandidate] = useState(null);
  const [hireForm, setHireForm] = useState({
    matricula: "", data_admissao: "", salario: 0, nivel: "Pleno",
    tipo_contrato: "CLT", turno: "diurno", data_nascimento: "", cargo: "",
  });

  // Interviews + timeline
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [timelineCand, setTimelineCand] = useState(null);
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [interviewForm, setInterviewForm] = useState({
    candidate_id: "", tipo: "rh", data_hora: "", modalidade: "presencial",
    entrevistador: "", local: "", link: "", status: "agendada", observacoes: "",
  });

  useEffect(() => {
    const params = companyParam ? { company_id: companyParam } : {};
    api.get("/sectors", { params }).then(({ data }) => setSectors(data));
  }, [companyParam]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const [v, c, d] = await Promise.all([
        api.get("/vacancies", { params }),
        api.get("/candidates", { params: search.trim() ? { search: search.trim() } : {} }),
        api.get("/vacancies/dashboard", { params: companyParam ? { company_id: companyParam } : {} }),
      ]);
      setVacs(v.data); setCands(c.data); setDash(d.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyParam, statusFilter]);
  useEffect(() => {
    const t = setTimeout(load, 400); return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  // ---- Vacancies CRUD ----
  const openNewVac = () => {
    setEditing(null);
    setForm({ ...emptyVac, company_id: companyParam || companies[0]?.id || "" });
    setOpen(true);
  };
  const openEditVac = (v) => { setEditing(v); setForm({ ...emptyVac, ...v }); setOpen(true); };
  const saveVac = async () => {
    try {
      const payload = {
        ...form,
        quantidade: Number(form.quantidade || 1),
        salario_previsto: Number(form.salario_previsto || 0),
      };
      if (editing) {
        await api.put(`/vacancies/${editing.id}`, payload);
        toast.success("Vaga atualizada");
      } else {
        await api.post("/vacancies", payload);
        toast.success("Vaga criada");
      }
      setOpen(false); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };
  const transitionVac = async (v, status, label) => {
    const motivo = status === "reprovada" ? window.prompt("Motivo da reprovação:") || "" : "";
    try {
      await api.post(`/vacancies/${v.id}/transition`, { status, motivo });
      toast.success(`Vaga ${label.toLowerCase()}`);
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const publicUrl = (vid) => `${window.location.origin}/carreiras/${vid}`;

  const togglePublish = async (v) => {
    const willPublish = !v.publicada;
    if (willPublish && !["aprovada", "em_recrutamento", "em_triagem", "em_entrevista"].includes(v.status)) {
      toast.error("A vaga precisa estar aprovada ou em recrutamento para ser publicada.");
      return;
    }
    try {
      await api.post(`/vacancies/${v.id}/publish`, { publicada: willPublish });
      if (willPublish) {
        try { await navigator.clipboard.writeText(publicUrl(v.id)); } catch (e) {
          // Ignore clipboard errors (e.g., permission denied in insecure contexts)
        }
        toast.success("Vaga publicada no Portal de Carreiras — link copiado!");
      } else {
        toast.success("Vaga removida do Portal de Carreiras.");
      }
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const copyPublicLink = async (v) => {
    try {
      await navigator.clipboard.writeText(publicUrl(v.id));
      toast.success("Link público copiado para a área de transferência");
    } catch (e) {
      toast.error("Não foi possível copiar. Copie manualmente: " + publicUrl(v.id));
    }
  };


  // ---- Candidates CRUD ----
  const openNewCand = (vacancy_id = "") => {
    setCEditing(null);
    setCForm({ ...emptyCand, vacancy_id });
    setCOpen(true);
  };
  const openEditCand = (c) => { setCEditing(c); setCForm({ ...emptyCand, ...c }); setCOpen(true); };
  const saveCand = async () => {
    try {
      const payload = {
        ...cForm,
        experiencia_anos: Number(cForm.experiencia_anos || 0),
        pretensao_salarial: Number(cForm.pretensao_salarial || 0),
      };
      if (cEditing) await api.put(`/candidates/${cEditing.id}`, payload);
      else await api.post("/candidates", payload);
      toast.success("Candidato salvo");
      setCOpen(false); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };
  const transitionCand = async (c, status, force = false) => {
    try {
      const body = force ? { status, force: true } : { status };
      await api.post(`/candidates/${c.id}/transition`, body);
      toast.success(`Candidato → ${CAND_STATUS[status]}`);
      load();
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Falha na transição";
      // If the block is 'precisa exame medico', offer force to admins
      if (status === "aprovado" && msg && msg.includes("Exame Médico")) {
        if (window.confirm(`${msg}\n\nAutorizar aprovação direta (requer perfil master/rh_admin)?`)) {
          return transitionCand(c, status, true);
        }
        return;
      }
      toast.error(msg);
    }
  };

  const deleteVacancy = async (v) => {
    if (!window.confirm(`Tem certeza que deseja excluir a vaga "${v.title}"?`)) return;
    try {
      const r = await api.delete(`/vacancies/${v.id}`);
      toast.success(`Vaga cancelada${r.data.active_candidates ? ` (${r.data.active_candidates} candidato(s) preservado(s))` : ""}`);
      load();
    } catch (ex) {
      const status = ex.response?.status;
      const detail = ex.response?.data?.detail;
      if (status === 409 && detail?.links) {
        const c = detail.links.active_candidates;
        if (window.confirm(`${detail.message}\n\nForçar exclusão? (Os candidatos serão preservados em banco_talentos)`)) {
          try {
            await api.delete(`/vacancies/${v.id}`, { params: { force: true } });
            toast.success(`Vaga excluída — ${c} candidato(s) preservado(s)`);
            load();
          } catch (e2) {
            toast.error(formatApiError(e2.response?.data?.detail));
          }
        }
      } else {
        toast.error(formatApiError(detail));
      }
    }
  };

  const deleteCandidate = async (c) => {
    if (!window.confirm(`Tem certeza que deseja excluir o candidato "${c.nome}"?\n\nMaster excluirá permanentemente. Outros perfis moverão para banco de talentos.`)) return;
    try {
      const r = await api.delete(`/candidates/${c.id}`);
      toast.success(r.data.hard ? "Candidato excluído permanentemente" : "Candidato movido para banco de talentos");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  // ---- Hire candidate → Employee ----
  const openHire = (c) => {
    const v = vacById[c.vacancy_id];
    setHireCandidate(c);
    setHireForm({
      matricula: "",
      data_admissao: new Date().toISOString().slice(0, 10),
      salario: v?.salario_previsto || c?.pretensao_salarial || 0,
      nivel: "Pleno",
      tipo_contrato: v?.tipo_contrato || "CLT",
      turno: "diurno",
      data_nascimento: "",
      cargo: v?.cargo || v?.title || "",
    });
    setHireOpen(true);
  };
  const confirmHire = async () => {
    try {
      const payload = { ...hireForm, salario: Number(hireForm.salario || 0) };
      await api.post(`/candidates/${hireCandidate.id}/hire`, payload);
      toast.success(`${hireCandidate.nome} efetivado(a) como colaborador(a)!`);
      setHireOpen(false);
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // ---- Timeline ----
  const openTimeline = async (c) => {
    setTimelineCand(c);
    setTimeline(null);
    setTimelineOpen(true);
    try {
      const { data } = await api.get(`/candidates/${c.id}/timeline`);
      setTimeline(data);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // ---- Interview ----
  const openInterview = (c) => {
    setInterviewForm({
      candidate_id: c.id,
      tipo: "rh",
      data_hora: new Date(Date.now() + 24 * 3600_000).toISOString().slice(0, 16),
      modalidade: "presencial",
      entrevistador: "", local: "", link: "",
      status: "agendada", observacoes: "",
    });
    setInterviewOpen(true);
  };
  const saveInterview = async () => {
    try {
      await api.post("/interviews", interviewForm);
      toast.success("Entrevista agendada");
      setInterviewOpen(false);
      if (timelineCand?.id === interviewForm.candidate_id) openTimeline(timelineCand);
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // ---- Export ----
  const exportExcel = async (kind) => {
    try {
      const url = kind === "vagas"
        ? `${API_BASE}/vacancies/export-excel${companyParam ? `?company_id=${companyParam}` : ""}`
        : `${API_BASE}/candidates/export-excel`;
      const token = localStorage.getItem("rh_token");
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Falha no export");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = kind === "vagas" ? "vagas.xlsx" : "candidatos.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Falha ao exportar"); }
  };

  const vacById = useMemo(() => Object.fromEntries(vacs.map((v) => [v.id, v])), [vacs]);

  return (
    <div className="space-y-6" data-testid="recruitment-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Talentos</div>
        <h1 className="font-display text-3xl font-semibold">Recrutamento &amp; Seleção</h1>
        <p className="mt-1 text-[13.5px] text-muted-foreground">
          Requisições, aprovações, vagas, candidatos e funil de seleção.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <K label="Total de vagas" value={dash?.totals?.total} icon={ClipboardList} tone="primary" />
        <K label="Abertas" value={dash?.totals?.abertas} icon={Clock} tone="warning" />
        <K label="Encerradas" value={dash?.totals?.encerradas} icon={CheckCircle2} tone="success" />
        <K label="Candidatos" value={dash?.totals?.candidatos} icon={Users2} tone="accent" />
        <K label="Tempo médio (d)" value={dash?.totals?.tempo_medio_dias} icon={Building2} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="vagas" data-testid="tab-vagas">Vagas ({vacs.length})</TabsTrigger>
          <TabsTrigger value="candidatos" data-testid="tab-candidatos">Candidatos ({cands.length})</TabsTrigger>
          <TabsTrigger value="funil" data-testid="tab-funil">Funil</TabsTrigger>
        </TabsList>

        {/* ------ VAGAS ------ */}
        <TabsContent value="vagas" className="m-0 space-y-4">
          <div className="rounded-lg border border-border bg-white p-4 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar vaga, cargo..." value={search}
                     onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="vac-search" />
            </div>
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-[200px]" data-testid="vac-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                {Object.keys(VAC_STATUS).map((k) => <SelectItem key={k} value={k}>{VAC_STATUS[k].label}</SelectItem>)}
              </SelectContent>
            </Select>
            {canEdit && (
              <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={openNewVac} data-testid="vac-new-btn">
                <Plus className="h-4 w-4 mr-2" /> Nova vaga
              </Button>
            )}
            <Button variant="outline" onClick={() => exportExcel("vagas")} data-testid="vac-export-btn">
              <FileDown className="h-3.5 w-3.5 mr-1.5" /> Exportar Excel
            </Button>
          </div>

          <div className="rounded-lg border border-border bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vaga</TableHead>
                  <TableHead>Setor</TableHead>
                  <TableHead>Qtde</TableHead>
                  <TableHead>Urgência</TableHead>
                  <TableHead>Salário</TableHead>
                  <TableHead>Candidatos</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? [1,2,3,4].map(i => <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-5" /></TableCell></TableRow>) :
                 vacs.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Nenhuma vaga cadastrada.</TableCell></TableRow> :
                 vacs.map((v) => {
                  const st = VAC_STATUS[v.status] || VAC_STATUS.solicitada;
                  const urg = v.urgencia === "alta" ? "bg-red-50 text-red-700 border-red-200" :
                              v.urgencia === "media" ? "bg-amber-50 text-amber-700 border-amber-200" :
                              "bg-slate-50 text-slate-700 border-slate-200";
                  return (
                    <TableRow key={v.id} className="rh-row" data-testid={`vac-row-${v.id}`}>
                      <TableCell>
                        <div className="font-medium flex items-center gap-2">
                          {v.title}
                          {v.publicada && (
                            <span
                              data-testid={`vac-public-badge-${v.id}`}
                              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[9.5px] uppercase tracking-[0.14em] text-emerald-700"
                              title="Publicada no portal externo"
                            >
                              <Globe className="h-2.5 w-2.5" /> Portal
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground">{v.cargo || "—"} · {v.tipo_contrato}</div>
                      </TableCell>
                      <TableCell className="text-[12.5px]">{v.sector_name || "—"}</TableCell>
                      <TableCell className="font-mono">{v.quantidade}</TableCell>
                      <TableCell><Badge variant="outline" className={urg}>{v.urgencia}</Badge></TableCell>
                      <TableCell className="font-mono text-[12.5px]">{BRL(v.salario_previsto)}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{v.candidates_count || 0}</Badge>
                      </TableCell>
                      <TableCell><Badge variant="outline" className={`ring-1 ring-inset ${st.cls}`}>{st.label}</Badge></TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`vac-actions-${v.id}`}><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => { setDetail(v); setDetailOpen(true); }}>
                              <Eye className="h-3.5 w-3.5 mr-2" /> Visualizar
                            </DropdownMenuItem>
                            {canEdit && <DropdownMenuItem onSelect={() => openEditVac(v)}><Pencil className="h-3.5 w-3.5 mr-2" /> Editar</DropdownMenuItem>}
                            <DropdownMenuItem onSelect={() => openNewCand(v.id)}><UserPlus className="h-3.5 w-3.5 mr-2" /> Novo candidato</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {canAdmin && v.status === "solicitada" && (
                              <DropdownMenuItem onSelect={() => transitionVac(v, "em_aprovacao", "Em aprovação")}>
                                <Send className="h-3.5 w-3.5 mr-2" /> Enviar para aprovação
                              </DropdownMenuItem>
                            )}
                            {canAdmin && v.status === "em_aprovacao" && (
                              <>
                                <DropdownMenuItem onSelect={() => transitionVac(v, "aprovada", "Aprovada")}>
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-2 text-emerald-600" /> Aprovar
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => transitionVac(v, "reprovada", "Reprovada")}>
                                  <XCircle className="h-3.5 w-3.5 mr-2 text-red-600" /> Reprovar
                                </DropdownMenuItem>
                              </>
                            )}
                            {canEdit && v.status === "aprovada" && (
                              <DropdownMenuItem onSelect={() => transitionVac(v, "em_recrutamento", "Em recrutamento")}>Iniciar recrutamento</DropdownMenuItem>
                            )}
                            {canEdit && ["em_recrutamento","em_triagem","em_entrevista"].includes(v.status) && (
                              <DropdownMenuItem onSelect={() => transitionVac(v, "encerrada", "Encerrada")}>
                                <CheckCircle2 className="h-3.5 w-3.5 mr-2" /> Encerrar vaga
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {canEdit && (
                              <DropdownMenuItem onSelect={() => togglePublish(v)} data-testid={`vac-publish-${v.id}`}>
                                {v.publicada
                                  ? <><Ban className="h-3.5 w-3.5 mr-2 text-amber-600" /> Despublicar do portal</>
                                  : <><Share2 className="h-3.5 w-3.5 mr-2 text-emerald-600" /> <b>Publicar no portal externo</b></>}
                              </DropdownMenuItem>
                            )}
                            {v.publicada && (
                              <DropdownMenuItem onSelect={() => copyPublicLink(v)} data-testid={`vac-copy-link-${v.id}`}>
                                <Link2 className="h-3.5 w-3.5 mr-2 text-indigo-600" /> Copiar link público
                              </DropdownMenuItem>
                            )}
                            {v.publicada && (
                              <DropdownMenuItem
                                onSelect={() => window.open(publicUrl(v.id), "_blank")}
                                data-testid={`vac-open-public-${v.id}`}
                              >
                                <Eye className="h-3.5 w-3.5 mr-2" /> Ver página pública
                              </DropdownMenuItem>
                            )}
                            {canAdmin && v.status !== "cancelada" && (
                              <DropdownMenuItem onSelect={() => transitionVac(v, "cancelada", "Cancelada")}>
                                <Ban className="h-3.5 w-3.5 mr-2 text-red-600" /> Cancelar vaga
                              </DropdownMenuItem>
                            )}
                            {canAdmin && (
                              <DropdownMenuItem
                                onSelect={() => deleteVacancy(v)}
                                className="text-rose-700 focus:text-rose-700 focus:bg-rose-50"
                                data-testid={`vac-delete-${v.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Excluir vaga
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                 })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ------ CANDIDATOS ------ */}
        <TabsContent value="candidatos" className="m-0 space-y-4">
          <div className="rounded-lg border border-border bg-white p-4 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar por nome ou e-mail..." value={search}
                     onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            {canEdit && (
              <Button onClick={() => openNewCand()} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="cand-new-btn">
                <UserPlus className="h-4 w-4 mr-2" /> Novo candidato
              </Button>
            )}
            <Button variant="outline" onClick={() => exportExcel("candidatos")} data-testid="cand-export-btn">
              <FileDown className="h-3.5 w-3.5 mr-1.5" /> Exportar Excel
            </Button>
          </div>

          <div className="rounded-lg border border-border bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Vaga</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Experiência</TableHead>
                  <TableHead>Pretensão</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-5" /></TableCell></TableRow>) :
                 cands.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Sem candidatos cadastrados.</TableCell></TableRow> :
                 cands.map((c) => (
                  <TableRow key={c.id} className="rh-row">
                    <TableCell>
                      <div className="font-medium">{c.nome}</div>
                      <div className="text-[11px] text-muted-foreground">{c.email || "—"}</div>
                    </TableCell>
                    <TableCell className="text-[12.5px]">{vacById[c.vacancy_id]?.title || "Banco de talentos"}</TableCell>
                    <TableCell className="text-[12.5px]">
                      {c.origem === "site" ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10.5px] uppercase tracking-[0.12em] text-emerald-700 font-medium">
                          <Globe className="h-2.5 w-2.5" /> Portal
                        </span>
                      ) : (
                        c.origem
                      )}
                    </TableCell>
                    <TableCell className="text-[12.5px]">{c.experiencia_anos || 0} anos</TableCell>
                    <TableCell className="font-mono text-[12.5px]">{BRL(c.pretensao_salarial)}</TableCell>
                    <TableCell><Badge variant="outline">{CAND_STATUS[c.status] || c.status}</Badge></TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`cand-actions-${c.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => openTimeline(c)} data-testid={`cand-timeline-${c.id}`}>
                            <HistoryIcon className="h-3.5 w-3.5 mr-2" /> Histórico / Timeline
                          </DropdownMenuItem>
                          {canEdit && <DropdownMenuItem onSelect={() => openEditCand(c)}><Pencil className="h-3.5 w-3.5 mr-2" /> Editar</DropdownMenuItem>}
                          {canEdit && (
                            <DropdownMenuItem onSelect={() => openInterview(c)} data-testid={`cand-interview-${c.id}`}>
                              <CalendarCheck className="h-3.5 w-3.5 mr-2 text-indigo-600" /> Agendar entrevista
                            </DropdownMenuItem>
                          )}
                          {canEdit && c.status === "aprovado" && (
                            <DropdownMenuItem onSelect={() => openHire(c)} data-testid={`cand-hire-${c.id}`}>
                              <Briefcase className="h-3.5 w-3.5 mr-2 text-emerald-600" /> <b>Efetivar contratação</b>
                            </DropdownMenuItem>
                          )}
                          {c.status === "contratado" && (
                            <DropdownMenuItem disabled>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-2 text-emerald-600" /> Contratado
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {canEdit && Object.keys(CAND_STATUS).filter(s => s !== c.status && s !== "contratado").map(s => (
                            <DropdownMenuItem key={s} onSelect={() => transitionCand(c, s)}>→ {CAND_STATUS[s]}</DropdownMenuItem>
                          ))}
                          {canEdit && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onSelect={() => deleteCandidate(c)}
                                className="text-rose-700 focus:text-rose-700 focus:bg-rose-50"
                                data-testid={`cand-delete-${c.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Excluir candidato
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                 ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ------ FUNIL ------ */}
        <TabsContent value="funil" className="m-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.keys(CAND_STATUS).map((st) => {
              const list = cands.filter(c => c.status === st);
              return (
                <div key={st} className="rounded-lg border border-border bg-white p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-[11.5px] uppercase tracking-wider text-muted-foreground font-medium">{CAND_STATUS[st]}</div>
                    <Badge variant="outline">{list.length}</Badge>
                  </div>
                  <div className="mt-3 space-y-2 max-h-[360px] overflow-y-auto">
                    {list.map(c => (
                      <div key={c.id} className="rounded-md border border-border p-2">
                        <div className="text-[12.5px] font-medium truncate">{c.nome}</div>
                        <div className="text-[10.5px] text-muted-foreground truncate">
                          {vacById[c.vacancy_id]?.title || "—"}
                        </div>
                      </div>
                    ))}
                    {list.length === 0 && <div className="text-[11px] text-muted-foreground text-center py-2">—</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Vacancy Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Editar vaga" : "Nova vaga"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FF label="Título" v={form.title} s={(v) => setForm({ ...form, title: v })} full />
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Empresa</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Setor</Label>
              <Select value={form.sector_id} onValueChange={(v) => setForm({ ...form, sector_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{sectors.filter(s => !form.company_id || s.company_id === form.company_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <FF label="Cargo" v={form.cargo} s={(v) => setForm({ ...form, cargo: v })} />
            <FF label="Gestor solicitante" v={form.gestor_solicitante} s={(v) => setForm({ ...form, gestor_solicitante: v })} />
            <FF label="Quantidade" v={form.quantidade} s={(v) => setForm({ ...form, quantidade: v })} type="number" />
            <FF label="Salário previsto" v={form.salario_previsto} s={(v) => setForm({ ...form, salario_previsto: v })} type="number" />
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Motivo</Label>
              <Select value={form.motivo} onValueChange={(v) => setForm({ ...form, motivo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aumento_quadro">Aumento de quadro</SelectItem>
                  <SelectItem value="substituicao">Substituição</SelectItem>
                  <SelectItem value="projeto">Projeto / Temporária</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Tipo contrato</Label>
              <Select value={form.tipo_contrato} onValueChange={(v) => setForm({ ...form, tipo_contrato: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CLT">CLT</SelectItem>
                  <SelectItem value="PJ">PJ</SelectItem>
                  <SelectItem value="Temporário">Temporário</SelectItem>
                  <SelectItem value="Estágio">Estágio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Urgência</Label>
              <Select value={form.urgencia} onValueChange={(v) => setForm({ ...form, urgencia: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baixa">Baixa</SelectItem>
                  <SelectItem value="media">Média</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FF label="Escolaridade" v={form.escolaridade} s={(v) => setForm({ ...form, escolaridade: v })} />
            <FF label="Experiência" v={form.experiencia} s={(v) => setForm({ ...form, experiencia: v })} />
            <FF label="Local de trabalho" v={form.local_trabalho} s={(v) => setForm({ ...form, local_trabalho: v })} full />
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Descrição</Label>
              <Textarea rows={2} value={form.descricao || ""} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Requisitos</Label>
              <Textarea rows={2} value={form.requisitos || ""} onChange={(e) => setForm({ ...form, requisitos: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={saveVac} data-testid="vac-save-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Candidate Dialog */}
      <Dialog open={cOpen} onOpenChange={setCOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{cEditing ? "Editar candidato" : "Novo candidato"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FF label="Nome" v={cForm.nome} s={(v) => setCForm({ ...cForm, nome: v })} full />
            <FF label="E-mail" v={cForm.email} s={(v) => setCForm({ ...cForm, email: v })} />
            <FF label="Telefone" v={cForm.telefone} s={(v) => setCForm({ ...cForm, telefone: v })} />
            <FF label="Cidade" v={cForm.cidade} s={(v) => setCForm({ ...cForm, cidade: v })} />
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Vaga</Label>
              <Select value={cForm.vacancy_id || "none"} onValueChange={(v) => setCForm({ ...cForm, vacancy_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Banco de talentos —</SelectItem>
                  {vacs.map((v) => <SelectItem key={v.id} value={v.id}>{v.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Origem</Label>
              <Select value={cForm.origem} onValueChange={(v) => setCForm({ ...cForm, origem: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="indicacao">Indicação</SelectItem>
                  <SelectItem value="site">Site</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="banco_talentos">Banco de talentos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FF label="Experiência (anos)" v={cForm.experiencia_anos} s={(v) => setCForm({ ...cForm, experiencia_anos: v })} type="number" />
            <FF label="Escolaridade" v={cForm.escolaridade} s={(v) => setCForm({ ...cForm, escolaridade: v })} />
            <FF label="Pretensão salarial" v={cForm.pretensao_salarial} s={(v) => setCForm({ ...cForm, pretensao_salarial: v })} type="number" />
            <FF label="URL do currículo" v={cForm.curriculo_url} s={(v) => setCForm({ ...cForm, curriculo_url: v })} full />
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Parecer RH</Label>
              <Textarea rows={2} value={cForm.parecer_rh || ""} onChange={(e) => setCForm({ ...cForm, parecer_rh: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Parecer do gestor</Label>
              <Textarea rows={2} value={cForm.parecer_gestor || ""} onChange={(e) => setCForm({ ...cForm, parecer_gestor: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCOpen(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={saveCand}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View detail */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{detail?.title}</DialogTitle>
            <DialogDescription asChild>
              <div>
                <Badge variant="outline" className={VAC_STATUS[detail?.status]?.cls}>{VAC_STATUS[detail?.status]?.label}</Badge>
              </div>
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-[13px]">
              <div className="grid grid-cols-2 gap-3">
                <D label="Cargo" v={detail.cargo} />
                <D label="Setor" v={detail.sector_name} />
                <D label="Quantidade" v={detail.quantidade} />
                <D label="Urgência" v={detail.urgencia} />
                <D label="Tipo contrato" v={detail.tipo_contrato} />
                <D label="Salário previsto" v={BRL(detail.salario_previsto)} />
                <D label="Gestor solicitante" v={detail.gestor_solicitante} />
                <D label="Local" v={detail.local_trabalho} />
                <D label="Criada em" v={fmtDate(detail.created_at)} />
                <D label="Candidatos" v={detail.candidates_count || 0} />
              </div>
              {detail.descricao && <div><Label className="text-[10.5px] uppercase tracking-wider">Descrição</Label><div className="mt-1 text-[12.5px]">{detail.descricao}</div></div>}
              {detail.requisitos && <div><Label className="text-[10.5px] uppercase tracking-wider">Requisitos</Label><div className="mt-1 text-[12.5px]">{detail.requisitos}</div></div>}
              <div className="flex justify-end pt-2">
                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  <Printer className="h-3.5 w-3.5 mr-1.5" /> Imprimir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hire Dialog */}
      <Dialog open={hireOpen} onOpenChange={setHireOpen}>
        <DialogContent className="max-w-2xl" data-testid="hire-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-emerald-600" /> Efetivar contratação
            </DialogTitle>
            <DialogDescription>
              {hireCandidate?.nome} · {vacById[hireCandidate?.vacancy_id]?.title || "Banco de talentos"}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FF label="Matrícula *" v={hireForm.matricula} s={(v) => setHireForm({ ...hireForm, matricula: v })} />
            <FF label="Data de admissão *" v={hireForm.data_admissao} s={(v) => setHireForm({ ...hireForm, data_admissao: v })} type="date" />
            <FF label="Cargo" v={hireForm.cargo} s={(v) => setHireForm({ ...hireForm, cargo: v })} />
            <FF label="Salário *" v={hireForm.salario} s={(v) => setHireForm({ ...hireForm, salario: v })} type="number" />
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Nível</Label>
              <Select value={hireForm.nivel} onValueChange={(v) => setHireForm({ ...hireForm, nivel: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Auxiliar","Trainee","Júnior","Pleno","Sênior","Master","Especialista","Líder","Supervisor"].map(n =>
                    <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Tipo de contrato</Label>
              <Select value={hireForm.tipo_contrato} onValueChange={(v) => setHireForm({ ...hireForm, tipo_contrato: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CLT">CLT</SelectItem>
                  <SelectItem value="PJ">PJ</SelectItem>
                  <SelectItem value="Temporário">Temporário</SelectItem>
                  <SelectItem value="Estágio">Estágio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Turno</Label>
              <Select value={hireForm.turno} onValueChange={(v) => setHireForm({ ...hireForm, turno: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="diurno">Diurno</SelectItem>
                  <SelectItem value="noturno">Noturno</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FF label="Data de nascimento" v={hireForm.data_nascimento} s={(v) => setHireForm({ ...hireForm, data_nascimento: v })} type="date" />
          </div>
          <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-[12px] text-emerald-900">
            Ao confirmar, o candidato será convertido em <b>colaborador ativo (em experiência)</b> no setor
            <b> {sectors.find(s => s.id === vacById[hireCandidate?.vacancy_id]?.sector_id)?.name || "—"}</b>,
            com status &quot;contratado&quot; no funil. Se a vaga atingir a quantidade solicitada, ela será encerrada automaticamente.
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHireOpen(false)}>Cancelar</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={confirmHire} data-testid="hire-confirm-btn">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Confirmar contratação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Interview Dialog */}
      <Dialog open={interviewOpen} onOpenChange={setInterviewOpen}>
        <DialogContent className="max-w-xl" data-testid="interview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-indigo-600" /> Agendar entrevista
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Tipo</Label>
              <Select value={interviewForm.tipo} onValueChange={(v) => setInterviewForm({ ...interviewForm, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rh">Entrevista RH</SelectItem>
                  <SelectItem value="gestor">Entrevista Gestor</SelectItem>
                  <SelectItem value="tecnica">Técnica</SelectItem>
                  <SelectItem value="painel">Painel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FF label="Data e hora *" v={interviewForm.data_hora} s={(v) => setInterviewForm({ ...interviewForm, data_hora: v })} type="datetime-local" />
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Modalidade</Label>
              <Select value={interviewForm.modalidade} onValueChange={(v) => setInterviewForm({ ...interviewForm, modalidade: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="presencial">Presencial</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="telefone">Telefone</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FF label="Entrevistador" v={interviewForm.entrevistador} s={(v) => setInterviewForm({ ...interviewForm, entrevistador: v })} />
            {interviewForm.modalidade === "presencial"
              ? <FF label="Local" v={interviewForm.local} s={(v) => setInterviewForm({ ...interviewForm, local: v })} full />
              : <FF label="Link da reunião" v={interviewForm.link} s={(v) => setInterviewForm({ ...interviewForm, link: v })} full />}
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Observações</Label>
              <Textarea rows={2} value={interviewForm.observacoes} onChange={(e) => setInterviewForm({ ...interviewForm, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInterviewOpen(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={saveInterview} data-testid="interview-save-btn">
              <CalendarCheck className="h-4 w-4 mr-1.5" /> Agendar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timeline Dialog */}
      <Dialog open={timelineOpen} onOpenChange={setTimelineOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="timeline-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <HistoryIcon className="h-5 w-5" /> Histórico · {timelineCand?.nome}
            </DialogTitle>
            <DialogDescription asChild>
              <div>
                {vacById[timelineCand?.vacancy_id]?.title || "Banco de talentos"} · Status atual:
                <Badge variant="outline" className="ml-1">{CAND_STATUS[timelineCand?.status] || timelineCand?.status}</Badge>
              </div>
            </DialogDescription>
          </DialogHeader>
          {!timeline ? (
            <div className="space-y-2"><Skeleton className="h-6 w-3/4" /><Skeleton className="h-6 w-full" /><Skeleton className="h-6 w-2/3" /></div>
          ) : (
            <div className="relative pl-7">
              <div className="absolute left-3 top-1 bottom-1 w-px bg-border" aria-hidden />
              {timeline.events.length === 0 && <div className="text-[12.5px] text-muted-foreground">Sem eventos.</div>}
              {timeline.events.map((ev, i) => {
                const kindCls = ev.kind === "entrevista" ? "bg-indigo-600" : ev.kind === "criado" ? "bg-emerald-600" : "bg-[#C5A059]";
                return (
                  <div key={i} className="relative pb-5 last:pb-0">
                    <span className={`absolute -left-[18px] top-1 h-3 w-3 rounded-full ring-4 ring-white ${kindCls}`} />
                    <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                      {fmtDateTime ? fmtDateTime(ev.at) : new Date(ev.at).toLocaleString("pt-BR")}
                      {ev.by && <span> · {ev.by}</span>}
                    </div>
                    <div className="text-[13px] font-medium mt-0.5">{ev.title}</div>
                    {ev.detail && <div className="text-[12px] text-muted-foreground mt-0.5">{ev.detail}</div>}
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            {timelineCand && timelineCand.status !== "contratado" && (
              <Button variant="outline" onClick={() => { setTimelineOpen(false); openInterview(timelineCand); }}>
                <CalendarCheck className="h-4 w-4 mr-1.5" /> Agendar nova entrevista
              </Button>
            )}
            <Button onClick={() => setTimelineOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

import DateInputBR from "@/components/DateInputBR";

const FF = ({ label, v, s, type = "text", full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <Label className="text-[11px] uppercase tracking-wider">{label}</Label>
    {type === "date"
      ? <DateInputBR value={v} onChange={s} />
      : <Input type={type} value={v ?? ""} onChange={(e) => s(e.target.value)} />}
  </div>
);
const D = ({ label, v }) => (
  <div><Label className="text-[10.5px] uppercase tracking-wider">{label}</Label><div className="mt-0.5">{v || "—"}</div></div>
);
function K({ label, value, icon: Icon, tone = "default" }) {
  const tones = { default: "bg-white", primary: "bg-[#234935] text-white", success: "bg-emerald-50", warning: "bg-amber-50", accent: "bg-[#F9F4E7]" };
  const labelColor = tone === "primary" ? "text-white/70" : "text-muted-foreground";
  const valueColor = tone === "primary" ? "text-white" : "text-foreground";
  return (
    <div className={`rounded-lg border border-border ${tones[tone]} p-4`}>
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-[10px] uppercase tracking-[0.22em] font-medium ${labelColor}`}>{label}</div>
          <div className={`mt-1.5 font-display text-[24px] font-semibold ${valueColor}`}>{value ?? 0}</div>
        </div>
        <Icon className={`h-4 w-4 ${tone === "primary" ? "text-[#C5A059]" : "text-muted-foreground"}`} />
      </div>
    </div>
  );
}
